// Admin theme (mode + accent palette) — persisted to localStorage.
// Keeps public site untouched; only applied inside <div className="admin-root"/>.
//
// SECURITY NOTE:
//   The only value stored here is a serialized preference object of the form
//   { mode: 'light'|'dark', palette: 'mustard'|'emerald'|... }. It contains no
//   PII, no tokens, no credentials — only per-browser visual preferences. It is
//   therefore appropriate to keep in localStorage. Authentication tokens live
//   in sessionStorage (see src/lib/tokenStore.js).
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { logger } from './logger';

const STORAGE_KEY = 'pg-admin-theme';

export const PALETTES = [
  {
    id: 'mustard',
    label: 'Mustard',
    swatch: '#F2B705',
    accent: '#F2B705',
    accentHover: '#FFC83D',
    accentFg: '#141414',
    accentSoft: 'rgba(242,183,5,0.15)',
    accentRing: 'rgba(242,183,5,0.35)',
  },
  {
    id: 'emerald',
    label: 'Emerald',
    swatch: '#10B981',
    accent: '#10B981',
    accentHover: '#059669',
    accentFg: '#FFFFFF',
    accentSoft: 'rgba(16,185,129,0.16)',
    accentRing: 'rgba(16,185,129,0.35)',
  },
  {
    id: 'royal',
    label: 'Royal Blue',
    swatch: '#3B82F6',
    accent: '#3B82F6',
    accentHover: '#2563EB',
    accentFg: '#FFFFFF',
    accentSoft: 'rgba(59,130,246,0.16)',
    accentRing: 'rgba(59,130,246,0.35)',
  },
  {
    id: 'crimson',
    label: 'Crimson',
    swatch: '#E11D48',
    accent: '#E11D48',
    accentHover: '#BE123C',
    accentFg: '#FFFFFF',
    accentSoft: 'rgba(225,29,72,0.16)',
    accentRing: 'rgba(225,29,72,0.35)',
  },
  {
    id: 'violet',
    label: 'Violet',
    swatch: '#7C3AED',
    accent: '#7C3AED',
    accentHover: '#6D28D9',
    accentFg: '#FFFFFF',
    accentSoft: 'rgba(124,58,237,0.16)',
    accentRing: 'rgba(124,58,237,0.35)',
  },
];

export const DEFAULT_THEME = { mode: 'light', palette: 'mustard' };

export const paletteById = (id) => PALETTES.find((p) => p.id === id) || PALETTES[0];

export const loadTheme = () => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return { ...DEFAULT_THEME };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_THEME, ...parsed };
  } catch (err) {
    logger.warn('theme: failed to load from storage', err);
    return { ...DEFAULT_THEME };
  }
};

export const saveTheme = (t) => {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(t)); }
  catch (err) { logger.warn('theme: failed to persist', err); }
};

const applyToDocument = (theme) => {
  if (typeof document === 'undefined') return;
  const p = paletteById(theme.palette);
  const root = document.documentElement;
  root.style.setProperty('--pg-admin-accent', p.accent);
  root.style.setProperty('--pg-admin-accent-hover', p.accentHover);
  root.style.setProperty('--pg-admin-accent-fg', p.accentFg);
  root.style.setProperty('--pg-admin-accent-soft', p.accentSoft);
  root.style.setProperty('--pg-admin-accent-ring', p.accentRing);
  root.setAttribute('data-admin-mode', theme.mode || 'light');
  root.setAttribute('data-admin-palette', p.id);
};

const clearFromDocument = () => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.removeAttribute('data-admin-mode');
  root.removeAttribute('data-admin-palette');
  root.style.removeProperty('--pg-admin-accent');
  root.style.removeProperty('--pg-admin-accent-hover');
  root.style.removeProperty('--pg-admin-accent-fg');
  root.style.removeProperty('--pg-admin-accent-soft');
  root.style.removeProperty('--pg-admin-accent-ring');
};

const ThemeContext = createContext({ theme: DEFAULT_THEME, setTheme: () => {}, setMode: () => {}, setPalette: () => {} });

export const AdminThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => loadTheme());

  useEffect(() => {
    applyToDocument(theme);
    saveTheme(theme);
    return () => { clearFromDocument(); };
  }, [theme]);

  const setTheme = useCallback((patch) => setThemeState((prev) => ({ ...prev, ...patch })), []);
  const setMode = useCallback((mode) => setThemeState((prev) => ({ ...prev, mode })), []);
  const setPalette = useCallback((palette) => setThemeState((prev) => ({ ...prev, palette })), []);

  const value = useMemo(() => ({ theme, setTheme, setMode, setPalette }), [theme, setTheme, setMode, setPalette]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useAdminTheme = () => useContext(ThemeContext);
